// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { decode } from 'cbor2';
import { CML as C } from '@lucid-evolution/lucid';
import { C as LC, toHex } from 'lucid-txpipe';

interface WithCIP30SignTxMethod {
  signTx: (tx: string, partialSign: boolean) => Promise<string>;
}

function hexToUint8Array(hexString: string) {
  if (hexString.length % 2 !== 0) {
    hexString = '0' + hexString;
  }

  const array = new Uint8Array(hexString.length / 2);
  for (let i = 0; i < hexString.length; i += 2) {
    array[i / 2] = parseInt(hexString.slice(i, i + 2), 16);
  }

  return array;
}

type CborFormat = 'legacy' | 'conway';
/**
 * Signs the given transaction with the provided wallet.
 *
 * @param cip30Wallet Object with the CIP30 `signTx` method, like the wallet coming from the client browser.
 * @param tx CBOR of the transaction we want to sign.
 * @param partialSign see partialSign `signTx` parameter description in CIP30 https://cips.cardano.org/cips/cip30/. Defaults to `false`. If this will not be the last signature of the transaction, this property must be set to `true`.
 * @returns CBOR of the signed transaction.
 */
async function signTx(
  cip30Wallet: WithCIP30SignTxMethod,
  tx: string,
  partialSign: boolean = false,
  cborFormat: CborFormat = 'legacy',
): Promise<string> {
  if (cborFormat === 'conway') {
    return signConwayTx(cip30Wallet, tx, partialSign);
  } else {
    return signLegacyTx(cip30Wallet, tx, partialSign);
  }
}

async function signLegacyTx(
  cip30Wallet: WithCIP30SignTxMethod,
  tx: string,
  partialSign: boolean = false,
) {
  const cTx = LC.Transaction.from_bytes(hexToUint8Array(tx));

  const witnessSetHex = await cip30Wallet.signTx(tx, partialSign);
  // VKey witness is a Map with one element which is at the key 0
  const witnessSetCBOR: Map<0, Set<[Uint8Array, Uint8Array]>> = decode(
    witnessSetHex,
    { encoding: 'hex' },
  );
  if (witnessSetCBOR.size === 0) {
    throw new Error('Empty witness set after signTx');
  }
  if (!witnessSetCBOR.has(0)) {
    throw new Error('Incorrect vkey witness CBOR format');
  }
  const witnessAsSet = witnessSetCBOR.get(0) as Set<[Uint8Array, Uint8Array]>;

  // Get the only pair [publicKey, signature] from the witness set
  const [key, sig] = Array.from(witnessAsSet)[0];

  // Add vkey witness to the transaction witness set and return signed tx
  const publicKey = LC.PublicKey.from_bytes(key);
  const vKey = LC.Vkey.new(publicKey);
  const signature = LC.Ed25519Signature.from_bytes(sig);
  const vKeyWitnesses = LC.Vkeywitnesses.new();
  vKeyWitnesses.add(LC.Vkeywitness.new(vKey, signature));
  const txWitnessSet = cTx.witness_set();
  txWitnessSet.set_vkeys(vKeyWitnesses);
  const signedTx = LC.Transaction.new(
    cTx.body(),
    txWitnessSet,
    cTx.auxiliary_data(),
  );
  return toHex(signedTx.to_bytes());
}

async function signConwayTx(
  cip30Wallet: WithCIP30SignTxMethod,
  tx: string,
  partialSign: boolean = false,
) {
  const cTx = C.Transaction.from_cbor_bytes(hexToUint8Array(tx));

  const witnessSetHex = await cip30Wallet.signTx(tx, partialSign);

  // VKey witness is a Map with one element which is at the key 0
  const witnessSetCBOR: Map<0, Set<[Uint8Array, Uint8Array]>> = decode(
    witnessSetHex,
    { encoding: 'hex' },
  );
  if (witnessSetCBOR.size === 0) {
    throw new Error('Empty witness set after signTx');
  }
  if (!witnessSetCBOR.has(0)) {
    throw new Error('Incorrect vkey witness CBOR format');
  }
  const witnessAsSet = witnessSetCBOR.get(0) as Set<[Uint8Array, Uint8Array]>;

  // Get the only pair [publicKey, signature] from the witness set
  const [key, sig] = Array.from(witnessAsSet)[0];

  // Add vkey witness to the transaction witness set and return signed tx
  const vKey = C.PublicKey.from_bytes(key);
  const signature = C.Ed25519Signature.from_raw_bytes(sig);
  const vKeyWitnesses = C.VkeywitnessList.new();
  vKeyWitnesses.add(C.Vkeywitness.new(vKey, signature));
  const txWitnessSet = cTx.witness_set();
  txWitnessSet.set_vkeywitnesses(vKeyWitnesses);
  const signedTx = C.Transaction.new(
    cTx.body(),
    txWitnessSet,
    true,
    cTx.auxiliary_data(),
  );
  return signedTx.to_cbor_hex();
}
export { signTx };
